import { useState } from 'react';
import { useCachedResource } from '../hooks/useCachedResource';
import { usePageRefresh } from '../context/PullToRefreshContext';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import TaskCard from '../components/TaskCard';
import { requirementsFromFlags } from '../components/RequirementPills';
import { useUserLocation } from '../hooks/useUserLocation';
import { setsApi, type ScheduledSet, type MySetsResponse } from '../services/api';
import { formatDateToLocal } from '../utils/timesheetRules';

/* COMMENTED OUT: Status badge component - was used by SetCard
function StatusBadge({ status }: { status: 'pending' | 'completed' }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        status === 'completed'
          ? 'bg-green-100 text-green-700'
          : 'bg-amber-100 text-amber-700'
      }`}
    >
      {status === 'completed' ? 'Completed' : 'Scheduled'}
    </span>
  );
}
*/

/* COMMENTED OUT: Team member badge - was used by SetCard
function TeamBadge({ role, name, isYou }: { role: string; name: string; isYou: boolean }) {
  const roleColors: Record<string, string> = {
    leader: 'bg-primary-100 text-primary-700',
    rep1: 'bg-blue-100 text-blue-700',
    rep2: 'bg-purple-100 text-purple-700',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${roleColors[role] || 'bg-slate-100 text-slate-700'}`}>
      {name}
      {isYou && <span className="text-[10px] opacity-70">(You)</span>}
    </span>
  );
}
*/

/* COMMENTED OUT: Set card component - replaced by TaskCard
function SetCard({ set, onClick }: { set: ScheduledSet; onClick: () => void }) {
  // Parse date without timezone shift by extracting date part and using noon local time
  const dateStr = set.set_date.split('T')[0];
  const setDate = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const isPast = setDate < today && set.status !== 'completed';
  const isToday = setDate.toDateString() === today.toDateString();

  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl bg-white p-4 text-left shadow-card transition-all hover:shadow-card-hover active:scale-[0.98] ${
        isPast ? 'border-l-4 border-red-500' : isToday ? 'border-l-4 border-primary-500' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-800">{set.store.store_name}</span>
            <StatusBadge status={set.status} />
          </div>
          <p className="mt-0.5 text-sm text-slate-500">
            {set.store.city}, {set.store.state}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            <span className="font-mono text-primary-600">#{set.project_id}</span>
            {' '}{set.project_description}
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-slate-800">
            {new Date(set.set_date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <div className="text-xs text-slate-500">
            {new Date(set.set_date.split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
        </div>
      </div>

      {set.team.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {set.team.map((member) => (
            <TeamBadge
              key={member.associate_id}
              role={member.role}
              name={member.name.split(' ')[0]}
              isYou={member.is_current_user}
            />
          ))}
        </div>
      )}

      {set.set_type_name && (
        <div className="mt-2 text-xs text-slate-500">
          Type: <span className="font-medium text-slate-700">{set.set_type_name}</span>
          {set.set_hours && <span className="ml-2">| {set.set_hours}h</span>}
        </div>
      )}

      {isPast && (
        <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Overdue
        </div>
      )}
    </button>
  );
}
*/

// Date range filter
function DateFilter({
  value,
  onChange,
}: {
  value: 'upcoming' | 'past' | 'all';
  onChange: (v: 'upcoming' | 'past' | 'all') => void;
}) {
  return (
    <div className="flex rounded-lg bg-slate-100 p-1">
      {(['upcoming', 'past', 'all'] as const).map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
            value === option
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          {option === 'upcoming' ? 'Upcoming' : option === 'past' ? 'Past' : 'All'}
        </button>
      ))}
    </div>
  );
}

export default function Sets() {
  const navigate = useNavigate();
  const { userLocation } = useUserLocation();
  const [dateFilter, setDateFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming');

  // Cache-first sets (#2057): paint the last-cached window instantly, revalidate
  // in the background. Fixed ±90-day window, so a single cache key covers it.
  const {
    data: setsData,
    isLoading,
    error,
    refetch: refetchSets,
  } = useCachedResource<MySetsResponse>(
    'sets-page',
    () => {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - 90); // 90 days ago
      const endDate = new Date(today);
      endDate.setDate(today.getDate() + 90); // 90 days ahead
      return setsApi.getMySets({
        start_date: formatDateToLocal(startDate),
        end_date: formatDateToLocal(endDate),
        status: 'all',
      });
    },
    []
  );

  // #2089: swipe-down revalidates the sets window.
  usePageRefresh(refetchSets);

  // Filter sets based on date
  const filteredSets = setsData?.sets.filter((set) => {
    const setDate = new Date(set.set_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'upcoming') {
      return setDate >= today || set.status === 'pending';
    }
    if (dateFilter === 'past') {
      return setDate < today && set.status === 'completed';
    }
    return true;
  }).sort((a, b) => {
    // Sort by date
    const dateA = new Date(a.set_date);
    const dateB = new Date(b.set_date);
    // Upcoming: ascending, Past: descending
    return dateFilter === 'past'
      ? dateB.getTime() - dateA.getTime()
      : dateA.getTime() - dateB.getTime();
  }) || [];

  // Group by date for display
  const groupedSets = filteredSets.reduce((acc, set) => {
    const dateKey = new Date(set.set_date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(set);
    return acc;
  }, {} as Record<string, ScheduledSet[]>);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <PageHeader
        title="My Sets"
        subtitle={
          isLoading
            ? 'Loading...'
            : `${setsData?.count || 0} scheduled sets`
        }
        onBack={() => navigate(-1)}
      />

      {/* Filter */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <DateFilter value={dateFilter} onChange={setDateFilter} />
      </div>

      {/* Summary stats */}
      {setsData && !isLoading && (
        <div className="grid grid-cols-3 gap-2 px-4 py-3">
          <div className="rounded-lg bg-white p-3 text-center shadow-card">
            <div className="text-2xl font-bold text-primary-600">
              {setsData.sets.filter((s) => s.status === 'pending').length}
            </div>
            <div className="text-xs text-slate-500">Pending</div>
          </div>
          <div className="rounded-lg bg-white p-3 text-center shadow-card">
            <div className="text-2xl font-bold text-green-600">
              {setsData.sets.filter((s) => s.status === 'completed').length}
            </div>
            <div className="text-xs text-slate-500">Completed</div>
          </div>
          <div className="rounded-lg bg-white p-3 text-center shadow-card">
            <div className="text-2xl font-bold text-slate-800">
              {setsData.sets.filter((s) => s.team.length > 1).length}
            </div>
            <div className="text-xs text-slate-500">Team Sets</div>
          </div>
        </div>
      )}

      {/* Sets List */}
      <main className="p-4">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="rounded-xl bg-red-50 p-4 text-center text-red-700">
            <p className="font-medium">Unable to load sets</p>
            <p className="mt-1 text-sm">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredSets.length === 0 && (
          <div className="rounded-xl bg-slate-100 p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
              <svg className="h-6 w-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-700">No Sets Found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {dateFilter === 'upcoming'
                ? "You don't have any upcoming sets scheduled."
                : dateFilter === 'past'
                ? "You don't have any completed sets."
                : "You don't have any sets assigned."}
            </p>
          </div>
        )}

        {/* Grouped Sets List */}
        {!isLoading && !error && Object.keys(groupedSets).length > 0 && (
          <div className="space-y-6">
            {Object.entries(groupedSets).map(([dateKey, sets]) => (
              <div key={dateKey}>
                <h3 className="mb-2 text-sm font-semibold text-slate-700">{dateKey}</h3>
                <div className="space-y-2">
                  {sets.map((set) => {
                    // Determine if overdue
                    const dateStr = set.set_date.split('T')[0];
                    const setDate = new Date(dateStr + 'T12:00:00');
                    const today = new Date();
                    today.setHours(12, 0, 0, 0);
                    const isPast = setDate < today && set.status !== 'completed';

                    return (
                      <TaskCard
                        key={set.projectset_id}
                        type="set"
                        store={{
                          store_id: set.store.store_id,
                          store_name: set.store.store_name,
                          legacy_store_id: set.store.legacy_store_id ? parseInt(set.store.legacy_store_id, 10) : undefined,
                          address: set.store.address,
                          city: set.store.city,
                          state: set.store.state,
                          lat: set.store.lat,
                          lon: set.store.lon,
                        }}
                        projectId={set.project_id}
                        projectDescription={set.project_description}
                        status={set.status === 'completed' ? 'completed' : isPast ? 'overdue' : 'pending'}
                        onClick={() => navigate(`/set/${set.projectset_id}`)}
                        setDate={set.set_date}
                        setHours={set.set_hours ?? undefined}
                        windowStart={set.display_specs?.window_start?.split('T')[0]}
                        windowEnd={set.display_specs?.window_end?.split('T')[0]}
                        setTypeName={set.set_type_name ?? undefined}
                        team={set.team}
                        requirements={requirementsFromFlags(set)}
                        userLocation={userLocation}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
